/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiCallOut, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useProfile } from '@modules/profile-lib/hooks'
import { useSessionStorageBackedState } from '@modules/utils/hooks/useLocalStorage'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import { useConfig } from '@modules/cui/ConfigContext'
import type { StackDeployment } from '@modules/ui-types'

import { isDeploymentVersionBelow9 } from './utils'

import type { FC, ReactNode } from 'react'

type CalloutContent =
  | {
      title: ReactNode
      description: ReactNode
    }
  | undefined

const CustomerEngineerCallout: FC<{ deployment: StackDeployment }> = ({ deployment }) => {
  const isAdminConsole = useConfig('APP_NAME') === 'adminconsole'
  const isTrialUser = useProfile({ enabled: !isAdminConsole })?.is_trial

  const billingDetails = useBillingDetails()
  const isCloudMonthlyBillingUser = billingDetails.data?.billing_model === `monthly`

  const [isCalloutVisible, setCalloutVisible] = useSessionStorageBackedState(
    `customerEngineerCalloutVisible`,
    true,
  )

  const getCalloutContent = (): CalloutContent => {
    if (isCloudMonthlyBillingUser && isDeploymentVersionBelow9(deployment)) {
      return {
        title: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-title.version-below-9'
            defaultMessage='Version 9.0 is here! Our Customer Engineers can help.'
          />
        ),
        description: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-description.version-below-9'
            defaultMessage='Get expert guidance on navigating breaking changes, upgrade paths, and compatibility questions. Connect with our specialists through <link>our online form</link>.'
            values={{
              link: (content) => <a href='https://www.elastic.co/contact/ce-help'>{content}</a>,
            }}
          />
        ),
      }
    }

    if (isTrialUser || isCloudMonthlyBillingUser) {
      return {
        title: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-title.default'
            defaultMessage='Need advice? Engage a Customer Engineer.'
          />
        ),
        description: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-description.default'
            defaultMessage='Use our <link>new online form</link> to get help with best practices, performance, and/or cost efficiency.'
            values={{
              link: (content) => <a href='https://www.elastic.co/contact/ce-help'>{content}</a>,
            }}
          />
        ),
      }
    }

    return undefined
  }

  const calloutContent = getCalloutContent()

  if (isCalloutVisible && calloutContent) {
    const { title, description } = calloutContent
    return (
      <Fragment>
        <EuiCallOut
          title={title}
          iconType='info'
          onDismiss={() => {
            setCalloutVisible(false)
          }}
        >
          <p>{description}</p>
        </EuiCallOut>
        <EuiSpacer />
      </Fragment>
    )
  }

  return null
}

export default CustomerEngineerCallout
